'use client';
import { useState, useEffect } from 'react';
import { liveUpdates, stories } from '@/lib/data';
import Label from '@/components/ui/Label';
import Link from 'next/link';
import StoryCard from '@/components/ui/StoryCard';

export default function LivePage() {
  const [lastUpdated, setLastUpdated] = useState('14:32');

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setLastUpdated(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const relatedStories = stories.filter(s => s.tags.includes('Peace Talks') || s.tags.includes('Geneva') || s.tags.includes('Ukraine')).slice(0, 3);

  return (
    <div>
      {/* LIVE HEADER */}
      <div className="bg-[#C8102E] px-5 py-5">
        <div className="max-w-[1400px] mx-auto flex items-start justify-between">
          <div className="flex-1">
            <div className="mb-3">
              <Label type="live" animate />
            </div>
            <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[28px] lg:text-[34px] text-white mb-3 leading-[1.2]">
              Geneva Peace Talks: Day 2 Live Updates
            </h1>
            <p className="font-['Source_Serif_4',Georgia,serif] text-[15px] text-white/85 max-w-[640px] leading-[1.6]">
              Delegates from 32 nations are gathered at the Palais des Nations for the most significant diplomatic event in a decade. A 48-hour ceasefire pause has been agreed. We are tracking every development.
            </p>
          </div>
          <div className="hidden md:block text-right">
            <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-white/75">Last updated: {lastUpdated}</p>
            <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-white/50 mt-1">Refreshes automatically</p>
          </div>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div className="max-w-[1400px] mx-auto px-5">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-0">
          {/* LIVE FEED */}
          <div className="lg:pr-8">
            {/* Jump links */}
            <div className="flex items-center gap-4 py-3 border-b border-[#D4D4D4] mb-0 overflow-x-auto scrollbar-none">
              <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] shrink-0">Jump to:</span>
              {[
                { label: 'Key Development', href: '#pinned' },
                { label: 'Analysis', href: '#analysis' },
                { label: 'Media', href: '#media' },
              ].map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#C8102E] font-semibold hover:underline shrink-0"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {liveUpdates.map((update) => {
              const isPinned = update.type === 'pinned';
              const isAnalyst = update.type === 'analyst';
              const isKeyMoment = update.type === 'keymoment';
              const isMedia = update.type === 'media';

              return (
                <div
                  key={update.id}
                  id={isPinned ? 'pinned' : isAnalyst ? 'analysis' : isMedia ? 'media' : undefined}
                  className={`grid grid-cols-[80px_1fr] border-b border-[#D4D4D4] ${
                    isPinned ? 'bg-[#FFFBEB] border-l-[3px] border-l-[#E07B00]' :
                    isKeyMoment ? 'border-l-[3px] border-l-[#1A1A1A]' :
                    isAnalyst ? 'bg-[#F5F4F0]' : 'bg-white'
                  }`}
                >
                  {/* Time col */}
                  <div className="px-4 py-5 border-r border-[#D4D4D4] flex flex-col items-center justify-start pt-5">
                    <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[13px] text-[#0D0D0D] text-center">
                      {update.time}
                    </span>
                    <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[10px] text-[#6B6B6B] mt-0.5 text-center">
                      {update.date}
                    </span>
                    {isPinned && (
                      <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[9px] text-[#E07B00] uppercase tracking-[0.05em] mt-2 text-center">
                        PINNED
                      </span>
                    )}
                  </div>
                  {/* Content col */}
                  <div className="px-6 py-5">
                    {isPinned && (
                      <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.08em] uppercase text-[#E07B00] mb-2">
                        📌 Key Development
                      </div>
                    )}
                    {isAnalyst && (
                      <div className="mb-2">
                        <Label type="analysis" />
                      </div>
                    )}
                    {isMedia && (
                      <div className="mb-2">
                        <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.08em] uppercase text-[#2D2D2D]">📷 PHOTO ESSAY</span>
                      </div>
                    )}
                    <h3 className={`font-['Playfair_Display',Georgia,serif] font-bold leading-[1.3] mb-2 ${
                      isKeyMoment ? 'text-[20px]' : 'text-[17px]'
                    } ${isAnalyst ? 'italic' : ''}`}>
                      {update.headline}
                    </h3>
                    <p className="font-['Source_Serif_4',Georgia,serif] text-[14px] text-[#2D2D2D] leading-[1.55]">
                      {update.body}
                    </p>
                    {isMedia && (
                      <div>
                        <div className="bg-[#D4D4D4] aspect-video mt-3 w-full" />
                        <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] italic text-[12px] text-[#6B6B6B] mt-1">
                          Rare access to the Palais des Nations negotiating corridors. Photo: Veritas News
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* SIDEBAR */}
          <div className="lg:border-l lg:border-[#D4D4D4] lg:pl-8 pt-6">
            {/* Background & Context */}
            <div className="mb-8">
              <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
                <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Background & Context</h3>
              </div>
              {[
                { title: 'What are the Geneva talks about?', label: 'explainer' },
                { title: 'Who are the key negotiators and what do they want?', label: 'analysis' },
                { title: 'Timeline: How this conflict began', label: 'feature' },
              ].map((item, i) => (
                <Link key={i} href="/world" className="block py-3 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group">
                  <span className={`inline-block font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.1em] uppercase px-1.5 py-0.5 mb-1.5 text-white ${
                    item.label === 'explainer' ? 'bg-[#0C7A6E]' : item.label === 'analysis' ? 'bg-[#1A1A1A]' : 'bg-[#E07B00]'
                  }`}>{item.label.toUpperCase()}</span>
                  <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[14px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{item.title}</h4>
                </Link>
              ))}
            </div>

            {/* Key Figures */}
            <div className="mb-8">
              <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
                <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Key Figures</h3>
              </div>
              {[
                { name: 'António Guterres', role: 'UN Secretary-General, Chief Mediator' },
                { name: 'Jean-Noël Barrot', role: 'French Foreign Minister' },
                { name: 'Dmytro Kuleba', role: 'Ukrainian Foreign Minister' },
              ].map((person, i) => (
                <div key={i} className="py-3 border-b border-[#D4D4D4]">
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[13px] text-[#0D0D0D]">{person.name}</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] mt-0.5">{person.role}</p>
                </div>
              ))}
            </div>

            {/* Follow This Story */}
            <div className="bg-[#F5F4F0] p-4 mb-6">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase mb-3">Follow This Story</h3>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] mb-3">Get email alerts for live updates from Geneva.</p>
              <div className="flex border border-[1.5px] border-[#0D0D0D] overflow-hidden">
                <input type="email" placeholder="Your email" className="flex-1 px-3 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] outline-none bg-transparent" />
                <button className="bg-[#C8102E] text-white px-3 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[11px] hover:bg-[#9E0C23] transition-colors whitespace-nowrap">
                  Get Alerts
                </button>
              </div>
            </div>

            {/* Related Topics */}
            <div>
              <div className="pb-2 border-b-[2.5px] border-[#0D0D0D] mb-3">
                <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Related Topics</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Peace Talks', 'Geneva', 'UN', 'Ceasefire', 'Diplomacy', 'NATO'].map(tag => (
                  <Link key={tag} href={`/topic/${tag.toLowerCase().replace(' ', '-')}`} className="border border-[#D4D4D4] px-3 py-1.5 text-[12px] font-['IBM_Plex_Sans',system-ui,sans-serif] text-[#2D2D2D] hover:bg-[#1A1A1A] hover:text-white hover:border-[#1A1A1A] transition-all">
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
